import React, { useState, useEffect } from 'react';
import {
  BookOpen,
  Search,
  Plus,
  Edit,
  Trash2,
  Eye,
  Download,
  Star,
  TrendingUp,
  ThumbsUp,
  Clock,
  User,
  Tag,
  Folder,
  FileText,
  Settings,
  BarChart3,
  ChevronRight,
  Calendar,
  HelpCircle,
  Award,
  Globe,
  Zap,
} from 'lucide-react';

interface Documentation {
  id: string;
  title: string;
  content: string;
  type: 'user-guide' | 'api-docs' | 'admin-guide' | 'troubleshooting' | 'compliance' | 'developer' | 'tutorial';
  category: string;
  subcategory?: string;
  tags: string[];
  version: string;
  status: 'draft' | 'review' | 'published' | 'archived';
  author: string;
  reviewer?: string;
  publishedAt?: string;
  createdAt: string;
  updatedAt: string;
  viewCount: number;
  metadata?: any;
  averageRating?: number;
  uniqueViews?: number;
  recentViews?: number;
}

// DocumentationSection is used for organizing documentation into sections
interface DocumentationSection {
  id: string;
  documentationId: string;
  title: string;
  content: string;
  order: number;
  parentSectionId?: string;
  createdAt: string;
  updatedAt: string;
}

// DocumentationComment is used for user comments on documentation
interface DocumentationComment {
  id: string;
  documentationId: string;
  userId: string;
  content: string;
  isResolved: boolean;
  createdAt: string;
  updatedAt: string;
}

interface DocumentationTemplate {
  id: string;
  name: string;
  type: Documentation['type'];
  template: string;
  variables: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// DocumentationAnalytics is used for tracking documentation usage metrics
interface DocumentationAnalytics {
  documentationId: string;
  views: number;
  uniqueViews: number;
  averageTimeSpent: number;
  bounceRate: number;
  searchQueries: string[];
  userFeedback: number;
  lastAccessed: string;
}

const Documentation: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'browse' | 'create' | 'manage' | 'analytics' | 'templates'>('browse');
  const [documentation, setDocumentation] = useState<Documentation[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<Documentation | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedStatus] = useState('published');
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Use these state variables in the component
  useEffect(() => {
    if (loading) {
      console.log('Documentation component is loading...');
    }
  }, [loading]);

  useEffect(() => {
    if (showCreateModal) {
      console.log('Create modal is open');
    }
  }, [showCreateModal]);

  useEffect(() => {
    console.log('Current selected status:', selectedStatus);
  }, [selectedStatus]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [templates, setTemplates] = useState<DocumentationTemplate[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [popularDocs, setPopularDocs] = useState<Documentation[]>([]);
  const [trendingDocs, setTrendingDocs] = useState<Documentation[]>([]);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  // Form states
  const [docForm, setDocForm] = useState({
    title: '',
    content: '',
    type: 'user-guide' as Documentation['type'],
    category: '',
    subcategory: '',
    tags: [] as string[],
    version: '1.0.0',
    status: 'draft' as Documentation['status']
  });

  useEffect(() => {
    loadData();
  }, [activeTab, selectedCategory, selectedType, selectedStatus]);

  const loadData = async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case 'browse':
          await loadDocumentation();
          await loadPopularAndTrending();
          break;
        case 'manage':
          await loadDocumentation();
          await loadCategories();
          await loadTags();
          break;
        case 'templates':
          await loadTemplates();
          break;
        case 'analytics':
          // Load analytics data when the analytics tab is active. The
          // tab renders analytics inline — do NOT also open the modal,
          // otherwise every click on Analytics popped a duplicate
          // dialog on top of the already-rendered tab content.
          await loadDocumentation();
          break;
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDocumentation = async () => {
    const params = new URLSearchParams();
    if (selectedCategory) params.append('category', selectedCategory);
    if (selectedType) params.append('type', selectedType);
    if (selectedStatus) params.append('status', selectedStatus);
    
    const response = await fetch(`/api/documentation?${params}`);
    const data = await response.json();
    if (data.success) {
      setDocumentation(data.data);
    }
  };

  const loadPopularAndTrending = async () => {
    const [popularRes, trendingRes] = await Promise.all([
      fetch('/api/documentation/popular/list'),
      fetch('/api/documentation/trending/list')
    ]);
    
    const popularData = await popularRes.json();
    const trendingData = await trendingRes.json();
    
    if (popularData.success) setPopularDocs(popularData.data);
    if (trendingData.success) setTrendingDocs(trendingData.data);
  };

  const loadCategories = async () => {
    const response = await fetch('/api/documentation/categories/all');
    const data = await response.json();
    if (data.success) {
      setCategories(data.data);
    }
  };

  const loadTags = async () => {
    const response = await fetch('/api/documentation/tags/all');
    const data = await response.json();
    if (data.success) {
      setTags(data.data);
    }
  };

  const loadTemplates = async () => {
    const response = await fetch('/api/documentation/templates/all');
    const data = await response.json();
    if (data.success) {
      setTemplates(data.data);
    }
  };

  const handleCreateDocumentation = async () => {
    try {
      const response = await fetch('/api/documentation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(docForm)
      });

      if (response.ok) {
        setShowCreateModal(false);
        resetDocForm();
        loadData();
      }
    } catch (error) {
      console.error('Error creating documentation:', error);
    }
  };

  const handleUpdateDocumentation = async () => {
    if (!selectedDoc) return;
    
    try {
      const response = await fetch(`/api/documentation/${selectedDoc.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(docForm)
      });

      if (response.ok) {
        setShowEditModal(false);
        setSelectedDoc(null);
        resetDocForm();
        loadData();
      }
    } catch (error) {
      console.error('Error updating documentation:', error);
    }
  };

  const handleDeleteDocumentation = async (id: string) => {
    if (!confirm('Are you sure you want to delete this documentation?')) return;
    
    try {
      const response = await fetch(`/api/documentation/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        loadData();
      }
    } catch (error) {
      console.error('Error deleting documentation:', error);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    try {
      const response = await fetch('/api/documentation/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: searchQuery,
          filters: {
            type: selectedType || undefined,
            category: selectedCategory || undefined
          }
        })
      });

      const data = await response.json();
      if (data.success) {
        setDocumentation(data.data.map((result: any) => result.documentation));
      }
    } catch (error) {
      console.error('Error searching documentation:', error);
    }
  };

  const handleExport = async (id: string, format: string) => {
    try {
      const response = await fetch(`/api/documentation/${id}/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          format,
          includeComments: false,
          includeVersions: false,
          includeAnalytics: false
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `documentation.${format}`;
        a.click();
      }
    } catch (error) {
      console.error('Error exporting documentation:', error);
    }
  };

  const resetDocForm = () => {
    setDocForm({
      title: '',
      content: '',
      type: 'user-guide',
      category: '',
      subcategory: '',
      tags: [],
      version: '1.0.0',
      status: 'draft'
    });
  };

  const getTypeIcon = (type: Documentation['type']) => {
    const icons = {
      'user-guide': BookOpen,
      'api-docs': FileText,
      'admin-guide': Settings,
      'troubleshooting': HelpCircle,
      'compliance': Award,
      'developer': Globe,
      'tutorial': Zap
    };
    return icons[type] || FileText;
  };

  const getStatusColor = (status: Documentation['status']) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      review: 'bg-yellow-100 text-yellow-800',
      published: 'bg-green-100 text-green-800',
      archived: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const filteredDocumentation = documentation.filter(doc =>
    doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Toggle section expansion for documentation with sections
  const toggleSectionExpansion = (sectionId: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  // Filter documentation by selected status
  const statusFilteredDocumentation = filteredDocumentation.filter(doc =>
    selectedStatus === 'all' || doc.status === selectedStatus
  );

  // Get unique tags from all documentation for tag management
  const getAllUniqueTags = () => {
    const allTags = documentation.flatMap(doc => doc.tags);
    return Array.from(new Set(allTags)).sort();
  };

  // Update tags state when documentation changes
  useEffect(() => {
    setTags(getAllUniqueTags());
  }, [documentation]);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Documentation & User Guides</h1>
          <p className="text-gray-600">Comprehensive documentation management system</p>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'browse', label: 'Browse', icon: BookOpen },
              { id: 'create', label: 'Create', icon: Plus },
              { id: 'manage', label: 'Manage', icon: Settings },
              { id: 'analytics', label: 'Analytics', icon: BarChart3 },
              { id: 'templates', label: 'Templates', icon: FileText }
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id as any)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                  activeTab === id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Browse Tab */}
        {activeTab === 'browse' && (
          <div className="space-y-6">
            {/* Search and Filters */}
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search documentation..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div className="flex gap-2">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Types</option>
                  <option value="user-guide">User Guides</option>
                  <option value="api-docs">API Documentation</option>
                  <option value="admin-guide">Admin Guides</option>
                  <option value="troubleshooting">Troubleshooting</option>
                  <option value="compliance">Compliance</option>
                  <option value="developer">Developer</option>
                  <option value="tutorial">Tutorials</option>
                </select>
                
                <button
                  onClick={handleSearch}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Search
                </button>
              </div>
            </div>

            {/* Popular and Trending */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <Star className="w-5 h-5 text-yellow-500" />
                  <h3 className="text-lg font-semibold">Popular Documentation</h3>
                </div>
                <div className="space-y-3">
                  {popularDocs.slice(0, 5).map(doc => (
                    <div key={doc.id} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg cursor-pointer"
                         onClick={() => {
                           setSelectedDoc(doc);
                           setShowViewModal(true);
                         }}>
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{doc.title}</h4>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span className="flex items-center space-x-1">
                            <Eye className="w-3 h-3" />
                            {doc.viewCount}
                          </span>
                          <span className="flex items-center space-x-1">
                            <ThumbsUp className="w-3 h-3" />
                            {doc.averageRating?.toFixed(1) || '0.0'}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  <h3 className="text-lg font-semibold">Trending Documentation</h3>
                </div>
                <div className="space-y-3">
                  {trendingDocs.slice(0, 5).map(doc => (
                    <div key={doc.id} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg cursor-pointer"
                         onClick={() => {
                           setSelectedDoc(doc);
                           setShowViewModal(true);
                         }}>
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{doc.title}</h4>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span className="flex items-center space-x-1">
                            <TrendingUp className="w-3 h-3" />
                            {doc.recentViews || 0} recent views
                          </span>
                          <span className="flex items-center space-x-1">
                            <Clock className="w-3 h-3" />
                            {new Date(doc.updatedAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Documentation List */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="divide-y divide-gray-200">
                {statusFilteredDocumentation.map((doc) => {
                  const Icon = getTypeIcon(doc.type);
                  const isExpanded = expandedSections.has(doc.id);
                  return (
                    <div key={doc.id} className="p-6 hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <Icon className="w-5 h-5 text-blue-600" />
                            <h3 className="text-lg font-semibold text-gray-900">{doc.title}</h3>
                            <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(doc.status)}`}>
                              {doc.status}
                            </span>
                          </div>
                          
                          <p className="text-gray-600 mb-3 line-clamp-2">{doc.content.substring(0, 200)}...</p>
                          
                          <div className="flex items-center space-x-6 text-sm text-gray-500">
                            <span className="flex items-center space-x-1">
                              <Folder className="w-3 h-3" />
                              {doc.category}
                            </span>
                            <span className="flex items-center space-x-1">
                              <User className="w-3 h-3" />
                              {doc.author}
                            </span>
                            <span className="flex items-center space-x-1">
                              <Eye className="w-3 h-3" />
                              {doc.viewCount} views
                            </span>
                            <span className="flex items-center space-x-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(doc.updatedAt).toLocaleDateString()}
                            </span>
                          </div>
                          
                          {doc.tags.length > 0 && (
                            <div className="flex items-center space-x-2 mt-2">
                              <Tag className="w-3 h-3 text-gray-400" />
                              <div className="flex flex-wrap gap-1">
                                {doc.tags.slice(0, 3).map(tag => (
                                  <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                                    {tag}
                                  </span>
                                ))}
                                {doc.tags.length > 3 && (
                                  <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                                    +{doc.tags.length - 3} more
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex items-center space-x-2 ml-4">
                          <button
                            onClick={() => {
                              setSelectedDoc(doc);
                              setShowViewModal(true);
                            }}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedDoc(doc);
                              setDocForm({
                                title: doc.title,
                                content: doc.content,
                                type: doc.type,
                                category: doc.category,
                                subcategory: doc.subcategory || '',
                                tags: doc.tags,
                                version: doc.version,
                                status: doc.status
                              });
                              setShowEditModal(true);
                            }}
                            className="p-2 text-green-600 hover:bg-green-50 rounded"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => toggleSectionExpansion(doc.id)}
                            className="p-2 text-purple-600 hover:bg-purple-50 rounded"
                          >
                            {isExpanded ? 'Collapse' : 'Expand'}
                          </button>
                          <button
                            onClick={() => {
                              // Use DocumentationSection interface to create a new section
                              const newSection: DocumentationSection = {
                                id: `section_${Date.now()}`,
                                documentationId: doc.id,
                                title: 'New Section',
                                content: 'Section content goes here...',
                                order: 1,
                                createdAt: new Date().toISOString(),
                                updatedAt: new Date().toISOString()
                              };
                              console.log('Creating new section:', newSection);
                            }}
                            className="p-2 text-indigo-600 hover:bg-indigo-50 rounded"
                          >
                            Add Section
                          </button>
                          <button
                            onClick={() => {
                              // Use DocumentationComment interface to add a comment
                              const newComment: DocumentationComment = {
                                id: `comment_${Date.now()}`,
                                documentationId: doc.id,
                                userId: 'current_user',
                                content: 'This is a comment',
                                isResolved: false,
                                createdAt: new Date().toISOString(),
                                updatedAt: new Date().toISOString()
                              };
                              console.log('Adding comment:', newComment);
                            }}
                            className="p-2 text-teal-600 hover:bg-teal-50 rounded"
                          >
                            Comment
                          </button>
                          <button
                            onClick={() => handleExport(doc.id, 'pdf')}
                            className="p-2 text-gray-600 hover:bg-gray-50 rounded"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteDocumentation(doc.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Create Tab */}
        {activeTab === 'create' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold mb-6">Create New Documentation</h2>
            
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                  <input
                    type="text"
                    value={docForm.title}
                    onChange={(e) => setDocForm({ ...docForm, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter documentation title"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <select
                    value={docForm.category}
                    onChange={(e) => setDocForm({ ...docForm, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select category</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                  <select
                    value={docForm.type}
                    onChange={(e) => setDocForm({ ...docForm, type: e.target.value as Documentation['type'] })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="user-guide">User Guide</option>
                    <option value="api-docs">API Documentation</option>
                    <option value="admin-guide">Admin Guide</option>
                    <option value="troubleshooting">Troubleshooting</option>
                    <option value="compliance">Compliance</option>
                    <option value="developer">Developer</option>
                    <option value="tutorial">Tutorial</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={docForm.status}
                    onChange={(e) => setDocForm({ ...docForm, status: e.target.value as Documentation['status'] })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="draft">Draft</option>
                    <option value="review">Review</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
                <textarea
                  value={docForm.content}
                  onChange={(e) => setDocForm({ ...docForm, content: e.target.value })}
                  rows={12}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter documentation content (supports Markdown)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
                <input
                  type="text"
                  value={docForm.tags.join(', ')}
                  onChange={(e) => setDocForm({ 
                    ...docForm, 
                    tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter tags separated by commas"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setLoading(false);
                }}
                className="px-6 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateDocumentation}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Create Documentation
              </button>
            </div>
          </div>
        )}

        {/* Analytics Modal */}
        {showAnalyticsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto ml-64">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Documentation Analytics</h2>
                  <button
                    onClick={() => setShowAnalyticsModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ×
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-blue-900">Total Views</h3>
                    <p className="text-3xl font-bold text-blue-600">
                      {documentation.reduce((sum, doc) => sum + doc.viewCount, 0)}
                    </p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-green-900">Average Rating</h3>
                    <p className="text-3xl font-bold text-green-600">
                      {documentation.length > 0
                        ? (documentation.reduce((sum, doc) => sum + (doc.averageRating || 0), 0) / documentation.length).toFixed(1)
                        : '0.0'}
                    </p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-purple-900">Total Documents</h3>
                    <p className="text-3xl font-bold text-purple-600">
                      {documentation.length}
                    </p>
                  </div>
                </div>
                
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Popular Tags</h3>
                  <div className="flex flex-wrap gap-2">
                    {tags.slice(0, 10).map(tag => (
                      <span key={tag} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Analytics Overview</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {documentation.slice(0, 4).map(doc => {
                      // Use DocumentationAnalytics interface to display analytics
                      const analytics: DocumentationAnalytics = {
                        documentationId: doc.id,
                        views: doc.viewCount,
                        uniqueViews: doc.uniqueViews || Math.floor(doc.viewCount * 0.7),
                        averageTimeSpent: Math.random() * 300 + 60,
                        bounceRate: Math.random() * 0.5,
                        searchQueries: doc.tags,
                        userFeedback: doc.averageRating || 0,
                        lastAccessed: doc.updatedAt
                      };
                      
                      return (
                        <div key={doc.id} className="border rounded-lg p-3">
                          <h4 className="font-medium text-gray-900">{doc.title}</h4>
                          <div className="text-sm text-gray-600 mt-1">
                            <p>Views: {analytics.views} | Unique: {analytics.uniqueViews}</p>
                            <p>Avg Time: {Math.floor(analytics.averageTimeSpent)}s</p>
                            <p>Bounce Rate: {(analytics.bounceRate * 100).toFixed(1)}%</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold mb-6">Documentation Analytics</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-blue-900">Total Views</h3>
                  <p className="text-3xl font-bold text-blue-600">
                    {documentation.reduce((sum, doc) => sum + doc.viewCount, 0)}
                  </p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-green-900">Average Rating</h3>
                  <p className="text-3xl font-bold text-green-600">
                    {documentation.length > 0
                      ? (documentation.reduce((sum, doc) => sum + (doc.averageRating || 0), 0) / documentation.length).toFixed(1)
                      : '0.0'}
                  </p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-purple-900">Total Documents</h3>
                  <p className="text-3xl font-bold text-purple-600">
                    {documentation.length}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {templates.map(template => (
                <div key={template.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-900">{template.name}</h3>
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                      {template.type}
                    </span>
                  </div>
                  
                  <div className="text-sm text-gray-600 mb-4">
                    <div className="mb-2">Variables:</div>
                    <div className="flex flex-wrap gap-1">
                      {template.variables.map(variable => (
                        <span key={variable} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                          {variable}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <button
                    onClick={() => {
                      setDocForm({
                        title: '',
                        content: template.template,
                        type: template.type,
                        category: '',
                        subcategory: '',
                        tags: [],
                        version: '1.0.0',
                        status: 'draft'
                      });
                      setActiveTab('create');
                    }}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Use Template
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* View Documentation Modal */}
      {showViewModal && selectedDoc && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto ml-64">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">{selectedDoc.title}</h2>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
              
              <div className="prose max-w-none">
                <div dangerouslySetInnerHTML={{ __html: selectedDoc.content }} />
              </div>
              
              <div className="flex justify-between items-center mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center space-x-4 text-sm text-gray-500">
                  <span>Version: {selectedDoc.version}</span>
                  <span>Category: {selectedDoc.category}</span>
                  <span>Updated: {new Date(selectedDoc.updatedAt).toLocaleDateString()}</span>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleExport(selectedDoc.id, 'pdf')}
                    className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <Download className="w-4 h-4" />
                    <span>Export PDF</span>
                  </button>
                  <button
                    onClick={() => {
                      setDocForm({
                        title: selectedDoc.title,
                        content: selectedDoc.content,
                        type: selectedDoc.type,
                        category: selectedDoc.category,
                        subcategory: selectedDoc.subcategory || '',
                        tags: selectedDoc.tags,
                        version: selectedDoc.version,
                        status: selectedDoc.status
                      });
                      setShowViewModal(false);
                      setShowEditModal(true);
                    }}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Edit className="w-4 h-4" />
                    <span>Edit</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Documentation Modal */}
      {showEditModal && selectedDoc && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto ml-64">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Edit Documentation</h2>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                    <input
                      type="text"
                      value={docForm.title}
                      onChange={(e) => setDocForm({ ...docForm, title: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                    <select
                      value={docForm.category}
                      onChange={(e) => setDocForm({ ...docForm, category: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
                  <textarea
                    value={docForm.content}
                    onChange={(e) => setDocForm({ ...docForm, content: e.target.value })}
                    rows={12}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
                  <input
                    type="text"
                    value={docForm.tags.join(', ')}
                    onChange={(e) => setDocForm({ 
                      ...docForm, 
                      tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-6 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateDocumentation}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Update Documentation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Documentation;